import { ApiClient } from '../api-client'

// Client for Analytics Report views (DEV-2377, §2). Backend enforces the
// ownership/permission rules; ids/config are opaque round-trips.

export type AnalyticsViewScope = 'default' | 'private' | 'shared'

export interface AnalyticsViewDTO {
	id: string
	account_id: string
	asset_id: string
	name: string
	scope: AnalyticsViewScope
	owner_user_id: string | null
	/** Owner display name, resolved server-side (null for seeded defaults). */
	owner_name?: string | null
	config: Record<string, unknown>
	/** Default views only: menu position; the lowest is the fallback default. */
	sort_order?: number | null
}

export interface AnalyticsViewsList {
	default: AnalyticsViewDTO[]
	private: AnalyticsViewDTO[]
	shared: AnalyticsViewDTO[]
	defaultViewId: string | null
}

const base = (accountId: string, assetId: string) =>
	`/accounts/${accountId}/assets/${assetId}/analytics-views`

const list = async (
	accountId: string,
	assetId: string
): Promise<AnalyticsViewsList> => {
	const { data } = await ApiClient.client.get(base(accountId, assetId))
	return data.data
}

const create = async (
	accountId: string,
	assetId: string,
	payload: { name: string; scope: 'private' | 'shared'; config: Record<string, unknown> }
): Promise<AnalyticsViewDTO> => {
	const { data } = await ApiClient.client.post(base(accountId, assetId), payload)
	return data.data
}

const update = async (
	accountId: string,
	assetId: string,
	id: string,
	patch: {
		name?: string
		scope?: 'private' | 'shared'
		config?: Record<string, unknown>
	}
): Promise<AnalyticsViewDTO> => {
	const { data } = await ApiClient.client.patch(`${base(accountId, assetId)}/${id}`, patch)
	return data.data
}

const remove = async (accountId: string, assetId: string, id: string): Promise<void> => {
	await ApiClient.client.delete(`${base(accountId, assetId)}/${id}`)
}

const duplicate = async (
	accountId: string,
	assetId: string,
	id: string,
	name?: string
): Promise<AnalyticsViewDTO> => {
	const { data } = await ApiClient.client.put(`${base(accountId, assetId)}/${id}/duplicate`, {
		name
	})
	return data.data
}

const setDefault = async (
	accountId: string,
	assetId: string,
	viewId: string
): Promise<void> => {
	await ApiClient.client.put(`${base(accountId, assetId)}/prefs/default`, { viewId })
}

export const AnalyticsViews = {
	list,
	create,
	update,
	remove,
	duplicate,
	setDefault
}
